import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	Box,
	Tabs,
	Tab,
	Typography,
	Button,
	Chip,
	Paper,
	Collapse,
	ToggleButton,
	ToggleButtonGroup,
	Alert,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { makeStyles } from "@mui/styles";
import {
	ArrowBack,
	Edit,
	Print,
	ExpandMore,
	ExpandLess,
} from "@mui/icons-material";

import { getVehicles } from "../../reducers/VehiclesReducer";
import {
	createWorkOrder,
	updateWorkOrder,
	deleteWorkOrder,
	closeWorkOrder,
	reopenWorkOrder,
} from "../../reducers/WorkOrdersReducer";
import {
	createInspection,
	updateInspection,
	deleteInspection,
	resolveInspection,
} from "../../reducers/InspectionsReducer";
import { checkInOutEquipment } from "../../reducers/EquipmentsReducer";
import { notify } from "../../reducers/NotifierReducer";
import {
	StatusBadge,
	formatDate,
	getServiceStatus,
	daysBetween,
	getCodeId,
} from "../../utils/functions";
import {
	getEmployees,
	getEquipmentByVehicle,
	getEquipments,
	getFundingSources,
	getInspectionsByVehicle,
	getVehicleAssignmentHistory,
	getWorkOrdersByVehicle,
} from "../../utils/commonServices";
import {
	getWorkOrderTypeOptions,
	getWorkOrderStatusOptions,
} from "../WorkOrders/useWorkOrdersData";
import {
	getInspectionTypeOptions,
	getChecklistItems,
} from "../Inspections/useInspectionsData";
import {
	getEquipmentTypeOptions,
	getEquipmentStatusOptions,
	getEquipmentConditionOptions,
	getEquipmentCategoryOptions,
	getEquipmentServiceRequirementsOptions,
} from "../Equipments/useEquipmentsData";

import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import AddEditWorkOrderDialog from "../WorkOrders/AddEditWorkOrderDialog";
import CloseWorkOrderDialog from "../WorkOrders/CloseWorkOrderDialog";
import AddEditInspectionDialog from "../Inspections/AddEditInspectionDialog";
import InspectionDetailDialog from "../Inspections/InspectionDetailDialog";
import AddEditEquipmentDialog from "../Equipments/AddEditEquipmentDialog";
import InspectionsTab from "./InspectionsTab";
import WorkOrdersTab from "./WorkOrdersTab";
import ServiceDueTab from "./ServiceDueTab";
import AssignmentHistoryTab from "./AssignmentHistoryTab";
import NotesTab from "./NotesTab";
import EquipmentTab from "./EquipmentTab";
import AssignEquipmentDialog from "../Equipments/AssignEquipmentDialog";

const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
		flexDirection: "column",
		height: "100%",
		padding: theme.spacing(2),
	},
	header: {
		display: "flex",
		alignItems: "flex-start",
		justifyContent: "space-between",
		marginBottom: theme.spacing(2),
		flexWrap: "wrap",
		gap: theme.spacing(2),
	},
	headerLeft: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(2),
		flexWrap: "wrap",
	},
	unitNumber: {
		fontSize: 28,
		fontWeight: 800,
		color: theme.palette.text.primary,
	},
	vehicleTitle: {
		fontSize: 18,
		fontWeight: 600,
		color: theme.palette.text.secondary,
	},
	metaStrip: {
		display: "flex",
		gap: theme.spacing(1),
		flexWrap: "wrap",
		marginTop: theme.spacing(1),
	},
	metaPill: {
		display: "inline-flex",
		alignItems: "center",
		gap: 4,
		padding: "4px 10px",
		background: theme.palette.action.hover,
		borderRadius: 20,
		fontSize: 11,
		color: theme.palette.text.secondary,
	},
	metaPillLabel: {
		fontWeight: 700,
		textTransform: "uppercase",
		letterSpacing: "0.05em",
		marginRight: 4,
		color: theme.palette.text.disabled,
		fontSize: 9,
	},
	tabsContainer: {
		borderBottom: `1px solid ${theme.palette.divider}`,
		marginBottom: theme.spacing(2),
	},
	tabPanel: {
		minHeight: 400,
	},
	emptyState: {
		textAlign: "center",
		padding: theme.spacing(6),
		color: theme.palette.text.secondary,
	},
	emptyIcon: {
		fontSize: 48,
		marginBottom: theme.spacing(1),
	},
	tableWrap: {
		overflowX: "auto",
	},
	clickableRow: {
		cursor: "pointer",
		"&:hover": {
			backgroundColor: theme.palette.action.hover,
		},
	},
}));

// Tab Panel component
const TabPanel = ({ children, value, index, ...other }) => (
	<div
		role="tabpanel"
		hidden={value !== index}
		id={`vehicle-tabpanel-${index}`}
		aria-labelledby={`vehicle-tab-${index}`}
		{...other}
	>
		{value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
	</div>
);

// ─── Mileage Chart Component
const MileageChart = ({
	open,
	onToggle,
	chartType,
	onChartTypeChange,
	dataPoints,
	vehicle,
}) => {
	const theme = useTheme();
	const fmtOdo = (n) =>
		n != null ? `${Number(n).toLocaleString()} mi` : "—";

	const hasData = dataPoints.length > 0;
	const startOdo = vehicle?.startingMileage || 0;
	const curOdo = vehicle?.odometer || 0;
	const totalDriven = curOdo - startOdo;

	// SVG dimensions
	const W = 640,
		H = 130;
	const PL = 52,
		PR = 12,
		PT = 10,
		PB = 28;
	const CW = W - PL - PR,
		CH = H - PT - PB;

	const maxOdo = Math.max(...dataPoints.map((p) => p.odometer), 1) * 1.08;
	const toY = (o) => PT + CH - (o / maxOdo) * CH;
	const toX = (i) => PL + (i / Math.max(dataPoints.length - 1, 1)) * CW;

	return (
		<Paper
			sx={{
				mb: 2,
				borderRadius: 1,
				overflow: "hidden",
			}}
		>
			<Box
				onClick={onToggle}
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					p: 1.5,
					cursor: "pointer",
					"&:hover": { bgcolor: "action.hover" },
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography variant="body2" fontWeight={700}>
						📈 Mileage Over Time
					</Typography>
					{startOdo > 0 && curOdo > 0 && (
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ fontFamily: "monospace" }}
						>
							{fmtOdo(startOdo)} → {fmtOdo(curOdo)} ·{" "}
							{totalDriven.toLocaleString()} mi driven
						</Typography>
					)}
				</Box>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					{open && (
						<ToggleButtonGroup
							size="small"
							value={chartType}
							exclusive
							onChange={(_, v) => v && onChartTypeChange(v)}
							onClick={(e) => e.stopPropagation()}
						>
							<ToggleButton value="bar" sx={{ px: 1, py: 0.5 }}>
								☲
							</ToggleButton>
							<ToggleButton value="line" sx={{ px: 1, py: 0.5 }}>
								⤴
							</ToggleButton>
						</ToggleButtonGroup>
					)}
					{open ? <ExpandLess /> : <ExpandMore />}
				</Box>
			</Box>

			<Collapse in={open}>
				<Box sx={{ p: 2, pt: 0 }}>
					{!hasData ? (
						<Box
							sx={{
								textAlign: "center",
								py: 4,
								color: "text.secondary",
							}}
						>
							No mileage data — add odometer readings to work
							orders or inspections
						</Box>
					) : (
						<svg
							viewBox={`0 0 ${W} ${H}`}
							style={{
								width: "100%",
								height: "auto",
								display: "block",
							}}
						>
							<defs>
								<linearGradient
									id="mileLineGrad"
									x1="0"
									y1="0"
									x2="0"
									y2="1"
								>
									<stop
										offset="0%"
										stopColor={theme.palette.info.main}
										stopOpacity="0.2"
									/>
									<stop
										offset="100%"
										stopColor={theme.palette.info.main}
										stopOpacity="0.01"
									/>
								</linearGradient>
							</defs>

							{/* Grid lines */}
							{[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
								const y = PT + CH * (1 - f);
								return (
									<g key={i}>
										<line
											x1={PL}
											y1={y}
											x2={W - PR}
											y2={y}
											stroke={theme.palette.divider}
											strokeWidth={f === 0 ? 1.5 : 0.6}
											strokeDasharray={
												f === 0 ? "" : "3,3"
											}
										/>
										<text
											x={PL - 4}
											y={y + 3.5}
											textAnchor="end"
											style={{
												fontSize: 8,
												fill: theme.palette.text
													.disabled,
												fontFamily: "monospace",
											}}
										>
											{((maxOdo * f) / 1000).toFixed(0)}k
										</text>
									</g>
								);
							})}

							{/* Chart */}
							{chartType === "line" ? (
								<g>
									{dataPoints.length > 1 && (
										<>
											<path
												d={`M ${toX(0)},${PT + CH} ${dataPoints
													.map(
														(p, i) =>
															`L ${toX(i)},${toY(p.odometer)}`,
													)
													.join(
														" ",
													)} L ${toX(dataPoints.length - 1)},${PT + CH} Z`}
												fill="url(#mileLineGrad)"
											/>
											<polyline
												points={dataPoints
													.map(
														(p, i) =>
															`${toX(i)},${toY(p.odometer)}`,
													)
													.join(" ")}
												fill="none"
												stroke={theme.palette.info.main}
												strokeWidth={2}
												strokeLinejoin="round"
											/>
										</>
									)}
									{dataPoints.map((p, i) => {
										const prev =
											i > 0 ? dataPoints[i - 1] : null;
										const isDown =
											prev && p.odometer < prev.odometer;
										const color = isDown
											? theme.palette.error.main
											: p.source === "insp"
												? theme.palette.success.main
												: p.source === "current"
													? theme.palette.primary.main
													: p.source === "wo"
														? theme.palette.warning
																.main
														: theme.palette.info
																.main;
										return (
											<g key={i}>
												<circle
													cx={toX(i)}
													cy={toY(p.odometer)}
													r={4}
													fill={color}
													stroke={
														theme.palette.background
															.paper
													}
													strokeWidth={1.5}
												/>
												<title>{`${p.label} · ${fmtOdo(p.odometer)} · ${p.date?.slice(0, 10)}`}</title>
											</g>
										);
									})}
								</g>
							) : (
								<g>
									{dataPoints.map((p, i) => {
										const bw = Math.max(
											3,
											CW / dataPoints.length - 2,
										);
										const bh =
											(p.odometer / maxOdo) * CH || 2;
										const prev =
											i > 0 ? dataPoints[i - 1] : null;
										const isDown =
											prev && p.odometer < prev.odometer;
										const color = isDown
											? theme.palette.error.main
											: p.source === "insp"
												? theme.palette.success.main
												: p.source === "current"
													? theme.palette.primary.main
													: p.source === "wo"
														? theme.palette.warning
																.main
														: theme.palette.info
																.main;
										return (
											<g key={i}>
												<rect
													x={toX(i) - bw / 2}
													y={toY(p.odometer)}
													width={bw}
													height={bh}
													fill={color}
													rx={1}
													opacity={0.85}
												/>
												<title>{`${p.label} · ${fmtOdo(p.odometer)} · ${p.date?.slice(0, 10)}`}</title>
											</g>
										);
									})}
								</g>
							)}

							{/* Legend */}
							<g transform={`translate(${PL}, ${H - 8})`}>
								<circle
									cx={0}
									cy={0}
									r={4}
									fill={theme.palette.info.main}
								/>
								<text
									x={8}
									y={3}
									style={{
										fontSize: 8,
										fill: theme.palette.text.secondary,
									}}
								>
									Start
								</text>
								<circle
									cx={50}
									cy={0}
									r={4}
									fill={theme.palette.warning.main}
								/>
								<text
									x={58}
									y={3}
									style={{
										fontSize: 8,
										fill: theme.palette.text.secondary,
									}}
								>
									Work Order
								</text>
								<circle
									cx={120}
									cy={0}
									r={4}
									fill={theme.palette.success.main}
								/>
								<text
									x={128}
									y={3}
									style={{
										fontSize: 8,
										fill: theme.palette.text.secondary,
									}}
								>
									Inspection
								</text>
								<circle
									cx={190}
									cy={0}
									r={4}
									fill={theme.palette.primary.main}
								/>
								<text
									x={198}
									y={3}
									style={{
										fontSize: 8,
										fill: theme.palette.text.secondary,
									}}
								>
									Current
								</text>
							</g>
						</svg>
					)}
				</Box>
			</Collapse>
		</Paper>
	);
};

// ─── Main VehicleDetail Component
const VehicleDetail = ({
	vehicle,
	onBack,
	onEdit,
	refreshList,
	backLabel = "Fleet Vehicles",
}) => {
	const classes = useStyles();
	const theme = useTheme();
	const dispatch = useDispatch();

	// Redux state
	const userAgency = useSelector((state) => state.user.userAgency);
	const { userData } = useSelector((state) => state.user);
	const dictionary = useSelector((state) => state.dictionary.dictionary);
	const { vehicles } = useSelector(
		(state) => state.vehicles || { vehicles: [] },
	);

	const {
		codePerVehicleServiceRequirements: serviceRequirements,
		codePerWorkOrderTypes,
		codePerWorkOrderStatus,
		codePerVehicleStatus,
		codePerInspectionTypes,
		codePerDepartment,
	} = dictionary || {};

	// Local state
	const [tab, setTab] = useState(0);
	const [mileageChartOpen, setMileageChartOpen] = useState(false);
	const [mileageChartType, setMileageChartType] = useState("line");
	const [woChartOpen, setWoChartOpen] = useState(false);
	const [woChartType, setWoChartType] = useState("breakdown");

	// Inspection Chart state
	const [inspChartOpen, setInspChartOpen] = useState(false);
	const [inspChartType, setInspChartType] = useState("stacked");

	// Work Order Dialog states
	const [selectedWO, setSelectedWO] = useState(null);
	const [showWODialog, setShowWODialog] = useState(false);
	const [showViewWODialog, setShowViewWODialog] = useState(false);
	const [showDeleteWODialog, setShowDeleteWODialog] = useState(false);
	const [showCloseWODialog, setShowCloseWODialog] = useState(false);

	// Inspection Dialog states
	const [showInspectionDialog, setShowInspectionDialog] = useState(false);
	const [selectedInspection, setSelectedInspection] = useState(null);
	const [showInspectionDetail, setShowInspectionDetail] = useState(false);
	const [showDeleteInspectionDialog, setShowDeleteInspectionDialog] =
		useState(false);
	const [inspectionToDelete, setInspectionToDelete] = useState(null);
	const [inspectionMode, setInspectionMode] = useState("create");
	const [workOrders, setWorkOrders] = useState([]);
	const [inspections, setInspections] = useState([]);

	// EQUIPMENT DIALOG STATES

	const [viewEquipment, setViewEquipment] = useState(null);
	const [showAssignEquipment, setShowAssignEquipment] = useState(false);

	// Options data
	const [employees, setEmployees] = useState([]);
	const [fundingSources, setFundingSources] = useState([]);
	const [workOrderTypes, setWorkOrderTypes] = useState([]);
	const [statusOptions, setStatusOptions] = useState([]);
	const [vehicleStatusOptions, setVehicleStatusOptions] = useState([]);
	const [inspectionTypeOptions, setInspectionTypeOptions] = useState([]);
	const [checklistItems, setChecklistItems] = useState([]);

	// Equipment options
	const [equipmentTypeOptions, setEquipmentTypeOptions] = useState([]);
	const [equipmentStatusOptions, setEquipmentStatusOptions] = useState([]);
	const [equipmentConditionOptions, setEquipmentConditionOptions] = useState(
		[],
	);
	const [equipmentCategoryOptions, setEquipmentCategoryOptions] = useState(
		[],
	);
	const [equipmentServiceRequirements, setEquipmentServiceRequirements] =
		useState([]);

	// Data arrays
	const [equipment, setEquipment] = useState([]);
	const [allEquipment, setAllEquipment] = useState([]); // For assignment dialog
	const [assignmentHistory, setAssignmentHistory] = useState([]);

	// Division options
	const divisionOptions = useMemo(() => {
		return (
			codePerDepartment?.map((dept) => ({
				label: dept?.Code,
				value: dept?.Code,
			})) || []
		);
	}, [codePerDepartment]);

	// Vehicle options for dropdowns
	const vehicleOptions = useMemo(() => {
		return vehicles.map((v) => ({
			value: v.id,
			label: `${v.unitNumber} — ${v.year} ${v.make} ${v.model}`,
		}));
	}, [vehicles]);

	// Load options data
	useEffect(() => {
		const loadOptions = async () => {
			if (!userAgency || !dictionary) return;

			try {
				const [
					empList,
					fundingList,
					woTypes,
					woStatuses,
					inspTypes,
					checklist,
					eqTypes,
					eqStatuses,
					eqConditions,
					eqCategories,
					eqServiceReqs,
				] = await Promise.all([
					getEmployees(userAgency, true),
					getFundingSources(userAgency, true),
					getWorkOrderTypeOptions(dictionary),
					getWorkOrderStatusOptions(dictionary),
					getInspectionTypeOptions(dictionary),
					getChecklistItems(userAgency),
					getEquipmentTypeOptions(dictionary),
					getEquipmentStatusOptions(dictionary),
					getEquipmentConditionOptions(dictionary),
					getEquipmentCategoryOptions(dictionary),
					getEquipmentServiceRequirementsOptions(dictionary),
				]);

				setEmployees(empList || []);
				setFundingSources(fundingList || []);
				setWorkOrderTypes(woTypes || []);
				setStatusOptions(woStatuses || []);
				setInspectionTypeOptions(inspTypes || []);
				setChecklistItems(checklist || []);
				setEquipmentTypeOptions(eqTypes || []);
				setEquipmentStatusOptions(eqStatuses || []);
				setEquipmentConditionOptions(eqConditions || []);
				setEquipmentCategoryOptions(eqCategories || []);
				setEquipmentServiceRequirements(eqServiceReqs || []);

				// Vehicle status options
				const vehStatuses =
					codePerVehicleStatus?.map((status) => ({
						label: status?.Code,
						value: status?.Code,
						ptsCodeId: status?.ptsCodeId,
					})) || [];
				setVehicleStatusOptions(vehStatuses);
			} catch (error) {
				console.error("Error loading options:", error);
			}
		};

		loadOptions();
	}, [userAgency, dictionary, codePerVehicleStatus]);

	const loadEquipments = async () => {
		// Load equipment for this vehicle
		const [
			eqList,
			allEqList,
			allVehAssHisList,
			woList,
			insList,
		] = await Promise.all([
			getEquipmentByVehicle(vehicle.id),
			getEquipments(userAgency),
			getVehicleAssignmentHistory(vehicle?.id, userAgency),
			getWorkOrdersByVehicle(vehicle.id, userAgency),
			getInspectionsByVehicle(vehicle.id, userAgency),
		]);

		setEquipment(eqList);
		setAllEquipment(allEqList);
		setAssignmentHistory(allVehAssHisList);
		setWorkOrders(woList);
		setInspections(insList);
	};

	// Load related data for vehicle
	useEffect(() => {
		if (vehicle?.id && userAgency) {
			loadEquipments();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, vehicle?.id, userAgency]);

	// Refresh functions
	const refreshWorkOrders = useCallback(async () => {
		if (vehicle?.id && userAgency) {
			const woList = await getWorkOrdersByVehicle(vehicle.id, userAgency);
			setWorkOrders(woList);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, vehicle?.id, userAgency]);

	const refreshInspections = useCallback(async () => {
		if (vehicle?.id && userAgency) {
			const insList = await getInspectionsByVehicle(
				vehicle.id,
				userAgency,
			);
			setInspections(insList);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, vehicle?.id, userAgency]);

	const refreshEquipment = useCallback(async () => {
		if (vehicle?.id && userAgency) {
			// Refresh vehicle equipment
			const eqList = await getEquipmentByVehicle(vehicle.id);
			const allEqList = await getEquipments(userAgency);
			setEquipment(eqList);
			setAllEquipment(allEqList);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, vehicle?.id, userAgency]);

	const refreshAssignmentHistory = useCallback(async () => {
		if (vehicle?.id && userAgency) {
			// Refresh vehicle equipment
			const allVehAssHisList = await getVehicleAssignmentHistory(
				vehicle?.id,
				userAgency,
			);
			setAssignmentHistory(allVehAssHisList);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, vehicle?.id, userAgency]);

	// Filter data for this vehicle
	const vehWOs = useMemo(() => {
		return (workOrders || [])
			.filter((wo) => wo.vehicleId === vehicle?.id)
			.sort((a, b) => new Date(b.openedDate) - new Date(a.openedDate));
	}, [workOrders, vehicle?.id]);

	const vehInsps = useMemo(() => {
		return (inspections || [])
			.filter((i) => i.vehicleId === vehicle?.id)
			.sort((a, b) => new Date(b.date) - new Date(a.date));
	}, [inspections, vehicle?.id]);

	const vehAssignments = useMemo(() => {
		return (assignmentHistory || [])
			.filter((a) => a.vehicleId === vehicle?.id)
			.sort(
				(a, b) => new Date(b.assignedDate) - new Date(a.assignedDate),
			);
	}, [assignmentHistory, vehicle?.id]);

	// Service status computation
	const svcStatuses = useMemo(() => {
		if (!vehicle?.serviceRequirements || !serviceRequirements) return [];

		return vehicle.serviceRequirements
			.map((reqId) => {
				const req = serviceRequirements.find(
					(r) =>
						r.ptsCodeId === reqId ||
						r.Code === reqId,
				);
				if (!req) return null;

				const status = getServiceStatus(vehicle, req, vehWOs);
				return { req, ...status };
			})
			.filter(Boolean)
			.sort((a, b) => {
				const order = { Overdue: 0, "Due Soon": 1, OK: 2, Unknown: 3 };
				return (order[a.status] ?? 3) - (order[b.status] ?? 3);
			});
	}, [vehicle, serviceRequirements, vehWOs]);

	const hasOverdueService = svcStatuses.some((s) => s.status === "Overdue");
	const hasDueSoonService =
		!hasOverdueService && svcStatuses.some((s) => s.status === "Due Soon");

	// Check for unresolved inspections
	const unresolvedInspections = useMemo(() => {
		return vehInsps.filter(
			(i) =>
				(i.overallStatus === "Fail" ||
					i.overallStatus === "Needs Attention") &&
				!i.resolvedAt,
		);
	}, [vehInsps]);

	// Auto-switch to Service tab if overdue
	useEffect(() => {
		if (hasOverdueService) {
			setTab(3);
		} else if (unresolvedInspections.length > 0) {
			setTab(2);
		}
	}, [vehicle?.id, hasOverdueService, unresolvedInspections.length]);

	// Get employee display name
	const getEmployeeDisplay = useCallback(
		(employeeId) => {
			const emp = employees.find(
				(e) => e.value === employeeId || e.value === String(employeeId),
			);
			return emp ? emp.label : "—";
		},
		[employees],
	);

	// Format helpers
	const fmtMoney = (n) =>
		n != null ? `$${Number(n).toFixed(2).toLocaleString()}` : "—";
	const fmtMi = (n) => (n != null ? `${Number(n).toLocaleString()} mi` : "—");

	// Calculate totals
	const totalWOCost = vehWOs.reduce((s, w) => s + (w.cost || 0), 0);
	const openWOs = vehWOs.filter(
		(w) => (w.statusInfo?.Code || w.status) === "Open",
	).length;
	const closedWOs = vehWOs.filter(
		(w) => (w.statusInfo?.Code || w.status) === "Closed",
	).length;

	// Mileage data points for chart
	const mileageDataPoints = useMemo(() => {
		const points = [];

		if (vehicle?.purchaseDate && vehicle?.startingMileage) {
			points.push({
				date: vehicle.purchaseDate,
				odometer: vehicle.startingMileage,
				source: "start",
				label: "Purchase / Starting Mileage",
			});
		}

		vehWOs.forEach((wo) => {
			if (wo.odometerAtService && wo.openedDate) {
				points.push({
					date: wo.openedDate,
					odometer: wo.odometerAtService,
					source: "wo",
					label: `WO-${String(wo.id).slice(-4).padStart(4, "0")} · ${wo.typeInfo?.Code || wo.type || "Service"}`,
				});
			}
		});

		vehInsps.forEach((insp) => {
			if (insp.odometer && insp.date) {
				points.push({
					date: insp.date,
					odometer: insp.odometer,
					source: "insp",
					label: `Inspection · ${insp.typeInfo?.Code || insp.type || ""}`,
				});
			}
		});

		if (vehicle?.odometer) {
			points.push({
				date: new Date().toISOString(),
				odometer: vehicle.odometer,
				source: "current",
				label: "Current Odometer",
			});
		}

		const dayMap = {};
		points.forEach((p) => {
			const day = p.date?.slice(0, 10);
			if (!dayMap[day] || p.odometer > dayMap[day].odometer) {
				dayMap[day] = p;
			}
		});

		return Object.values(dayMap).sort(
			(a, b) => new Date(a.date) - new Date(b.date),
		);
	}, [vehicle, vehWOs, vehInsps]);

	// Vehicle option for dialogs
	const vehicleOption = useMemo(
		() => ({
			value: vehicle?.id,
			label: `${vehicle?.unitNumber} — ${vehicle?.year} ${vehicle?.make} ${vehicle?.model}`,
			id: vehicle?.id,
			unitNumber: vehicle?.unitNumber,
			year: vehicle?.year,
			make: vehicle?.make,
			model: vehicle?.model,
			odometer: vehicle?.odometer,
			status: vehicle?.status,
			serviceRequirements: vehicle?.serviceRequirements || [],
		}),
		[vehicle],
	);

	// WORK ORDER HANDLERS

	const handleAddWO = () => {
		setSelectedWO(null);
		setShowWODialog(true);
	};

	const handleViewWO = (wo) => {
		setSelectedWO(wo);
		setShowViewWODialog(true);
	};

	const handleEditWO = (wo) => {
		setSelectedWO(wo);
		setShowWODialog(true);
	};

	const handleDeleteWO = (wo) => {
		setSelectedWO(wo);
		setShowDeleteWODialog(true);
	};

	const handleCloseWO = (wo) => {
		setSelectedWO(wo);
		setShowCloseWODialog(true);
	};

	const handleReopenWO = async (wo) => {
		try {
			await dispatch(reopenWorkOrder(wo.id, userData.user.ptsUserID));
			dispatch(notify("Work order reopened successfully", "success"));
			refreshWorkOrders();
		} catch (error) {
			dispatch(notify("Failed to reopen work order", "error"));
		}
	};

	const handleSaveWO = async (payload) => {
		try {
			const typeId = getCodeId(payload.type, codePerWorkOrderTypes);
			const statusId = getCodeId(payload.status, codePerWorkOrderStatus);

			const data = {
				...payload,
				typeId,
				statusId,
				vehicleId: vehicle.id,
			};

			if (selectedWO) {
				await dispatch(
					updateWorkOrder(
						selectedWO.id,
						data,
						userAgency,
						userData.user.ptsUserID,
					),
				);
			} else {
				await dispatch(
					createWorkOrder(data, userAgency, userData.user.ptsUserID),
				);
			}

			dispatch(
				notify(
					`Work order ${selectedWO ? "updated" : "created"} successfully`,
					"success",
				),
			);
			setShowWODialog(false);
			setSelectedWO(null);
			refreshWorkOrders();
		} catch (error) {
			dispatch(notify("Failed to save work order", "error"));
		}
	};

	const handleConfirmDeleteWO = async () => {
		try {
			await dispatch(
				deleteWorkOrder(selectedWO.id, userData.user.ptsUserID),
			);
			dispatch(notify("Work order deleted successfully", "success"));
			setShowDeleteWODialog(false);
			setSelectedWO(null);
			refreshWorkOrders();
		} catch (error) {
			dispatch(notify("Failed to delete work order", "error"));
		}
	};

	const handleConfirmCloseWO = async (
		closedDate,
		newVehicleStatus,
		odometerAtService,
	) => {
		try {
			await dispatch(
				closeWorkOrder(
					selectedWO.id,
					closedDate,
					selectedWO.vehicleId,
					newVehicleStatus,
					odometerAtService,
					userData.user.ptsUserID,
				),
			);
			dispatch(notify("Work order closed successfully", "success"));
			setShowCloseWODialog(false);
			setSelectedWO(null);
			refreshWorkOrders();
		} catch (error) {
			dispatch(notify("Failed to close work order", "error"));
		}
	};

	const handleCreateWOFromService = (req) => {
		setSelectedWO({
			vehicleId: vehicle.id,
			type: req?.Code || "",
			serviceRequirementId: req?.ptsCodeId,
			status: "Open",
		});
		setShowWODialog(true);
	};

	// INSPECTION HANDLERS

	const handleAddInspection = () => {
		setSelectedInspection(null);
		setInspectionMode("create");
		setShowInspectionDialog(true);
	};

	const handleViewInspection = (insp) => {
		setSelectedInspection(insp);
		setShowInspectionDetail(true);
	};

	const handleEditInspection = (insp) => {
		setSelectedInspection(insp);
		setInspectionMode("edit");
		setShowInspectionDialog(true);
	};

	const handleDeleteInspection = (insp) => {
		setInspectionToDelete(insp);
		setShowDeleteInspectionDialog(true);
	};

	const handleConfirmDeleteInspection = async () => {
		try {
			await dispatch(
				deleteInspection(
					inspectionToDelete.id,
					userData.user.ptsUserID,
				),
			);
			dispatch(notify("Inspection deleted successfully", "success"));
			setShowDeleteInspectionDialog(false);
			setInspectionToDelete(null);
			refreshInspections();
		} catch (error) {
			dispatch(notify("Failed to delete inspection", "error"));
		}
	};

	const handleResolveInspection = (insp) => {
		setSelectedInspection(insp);
		setShowInspectionDetail(true);
	};

	const handleResolveInspectionFromDetail = async (resolveData) => {
		try {
			await dispatch(
				resolveInspection(
					selectedInspection.id,
					resolveData,
					userData.user.ptsUserID,
				),
			);

			setSelectedInspection((prev) => ({
				...prev,
				resolvedAt: resolveData.resolvedAt,
				resolvedBy: userData.user.ptsUserID,
				resolvedComments: resolveData.resolvedComments,
			}));

			dispatch(notify("Inspection resolved successfully", "success"));
			refreshInspections();
		} catch (error) {
			dispatch(notify("Failed to resolve inspection", "error"));
		}
	};

	const handleCreateWorkOrderFromInspection = (insp) => {
		const failedItems =
			insp.responses?.filter((r) => r.status === "Fail") || [];
		const failedItemNames = failedItems
			.map((f) => {
				const item = checklistItems.find(
					(c) => c.id === f.checklistItemId,
				);
				return `• ${item?.name || f.checklistItemId}${f.comment ? `: ${f.comment}` : ""}`;
			})
			.join("\n");

		const notes = failedItemNames
			? `Inspection failures:\n${failedItemNames}`
			: `Follow-up from inspection INS-${String(insp.id).slice(-4).padStart(4, "0")}`;

		setSelectedWO({
			vehicleId: vehicle.id,
			type: "Repair",
			status: "Open",
			notes: notes,
			inspectionId: insp.id,
		});
		setShowWODialog(true);
	};

	const handleSaveInspection = async (payload) => {
		try {
			const typeId = getCodeId(payload.type, codePerInspectionTypes);

			const data = {
				vehicleId: vehicle.id,
				perPersonnelId: payload.perPersonnelId,
				type: payload.type,
				typeId,
				date: payload.date,
				odometer: payload.odometer,
				overallStatus: payload.overallStatus,
				notes: payload.notes,
			};

			if (inspectionMode === "edit" && selectedInspection) {
				await dispatch(
					updateInspection(
						selectedInspection.id,
						data,
						payload.responses || [],
						payload.photos || [],
						userAgency,
						userData.user.ptsUserID,
					),
				);
				dispatch(notify("Inspection updated successfully", "success"));
			} else {
				await dispatch(
					createInspection(
						data,
						payload.responses || [],
						payload.photos || [],
						userAgency,
						userData.user.ptsUserID,
					),
				);
				dispatch(notify("Inspection created successfully", "success"));
			}

			setShowInspectionDialog(false);
			setSelectedInspection(null);
			setInspectionMode("create");
			refreshInspections();
			dispatch(getVehicles(0, 100, "id", "asc", "", {}, userAgency));
		} catch (error) {
			dispatch(notify("Failed to save inspection", "error"));
		}
	};

	// EQUIPMENT HANDLERS

	const handleViewEquipment = (eq) => {
		setViewEquipment(eq);
	};

	const handleAssignEquipment = async (assignData) => {
		try {
			await dispatch(
				checkInOutEquipment(
					assignData.equipmentId,
					{
						status: "Deployed",
						vehicleId: vehicle.id,
						perPersonnelId: null,
						location: `Vehicle ${vehicle.unitNumber}`,
						assignedDate: new Date().toISOString(),
					},
					userAgency,
					userData.user.ptsUserID,
				),
			);

			dispatch(notify("Equipment assigned successfully", "success"));
			refreshEquipment();
		} catch (error) {
			dispatch(notify("Failed to assign equipment", "error"));
		}
	};

	// PRINT HANDLER

	const handlePrint = () => {
		const printedBy =
			userData?.user?.Username || userData?.user?.FirstName || "Unknown";
		const empDisplay = getEmployeeDisplay(vehicle.perPersonnelId);
		const vehicleDisplay = `${vehicle.unitNumber} — ${vehicle.year} ${vehicle.make} ${vehicle.model}`;

		// Get funding source
		const fs = fundingSources.find(
			(f) => f.value === vehicle.fundingSourceId,
		);
		const fundingSourceName = fs?.label || "—";

		// Helper functions
		const d = (v) => v || "—";
		const sectionHead = (title) =>
			`<div class="section-head">${title}</div>`;
		const fieldRow = (label, value) =>
			`<div class="field-row"><span class="field-label">${label}</span><span class="field-value">${value}</span></div>`;

		const tableHTML = (headers, rows) => {
			if (!rows.length) return `<div class="empty-note">No records</div>`;
			return `<table><thead><tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr></thead><tbody>
			${rows.map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join("")}</tr>`).join("")}
		</tbody></table>`;
		};

		// ─── Work Orders Table ─
		const woTable = tableHTML(
			[
				"WO#",
				"Date",
				"Type",
				"Vendor",
				"Cost",
				"Status",
				"Downtime",
				"Notes",
			],
			vehWOs.map((wo) => {
				const downtime = wo.closedDate
					? daysBetween(wo.openedDate, wo.closedDate)
					: daysBetween(wo.openedDate, new Date().toISOString());
				return [
					`WO-${String(wo.id).slice(-4).padStart(4, "0")}`,
					formatDate(wo.openedDate),
					wo.typeInfo?.Code || wo.type || "—",
					d(wo.vendor),
					fmtMoney(wo.cost),
					wo.statusInfo?.Code || wo.status || "—",
					`${downtime}d`,
					(wo.notes || "").slice(0, 60) +
						(wo.notes?.length > 60 ? "…" : ""),
				];
			}),
		);

		// ─── Equipment Table ───
		const eqTable = tableHTML(
			["Type", "Serial #", "Make/Model", "Status", "Condition"],
			equipment.map((e) => [
				e.typeInfo?.Code || e.type || "—",
				d(e.serialNumber),
				[e.make, e.model].filter(Boolean).join(" ") || "—",
				e.statusInfo?.Code || e.status || "—",
				e.conditionInfo?.Code || e.condition || "—",
			]),
		);

		// ─── Inspections Table ─
		const inspTable = tableHTML(
			[
				"Date",
				"Type",
				"Odometer",
				"Result",
				"Officer",
				"Resolved",
				"Notes",
			],
			vehInsps.slice(0, 20).map((i) => {
				const officer = employees.find(
					(e) =>
						e.value === i.perPersonnelId ||
						e.value === String(i.perPersonnelId),
				);
				return [
					formatDate(i.date),
					i.typeInfo?.Code || i.type || "—",
					fmtMi(i.odometer),
					i.overallStatus || "—",
					officer?.label || "—",
					i.resolvedAt ? formatDate(i.resolvedAt) : "—",
					(i.notes || "").slice(0, 40) +
						(i.notes?.length > 40 ? "…" : ""),
				];
			}),
		);

		// ─── Service Requirements Table
		const svcTable = svcStatuses.length
			? tableHTML(
					["Service", "Interval", "Last Done", "Odometer", "Status"],
					svcStatuses.map((s) => [
						s.req?.Code || "—",
						s.req?.NumericValue && s.req?.Unit
							? `Every ${s.req.NumericValue.toLocaleString()} ${s.req.Unit}`
							: "—",
						formatDate(s.lastDate),
						s.lastOdometer != null ? fmtMi(s.lastOdometer) : "—",
						s.status || "—",
					]),
				)
			: `<div class="empty-note">No service requirements configured</div>`;

		// ─── Assignment History Table
		const assignTable = tableHTML(
			[
				"Officer",
				"Badge",
				"Division",
				"Assigned",
				"Unassigned",
				"Duration",
				"Notes",
			],
			vehAssignments.map((a) => {
				const officer = employees.find(
					(e) =>
						e.value === a.perPersonnelId ||
						e.value === String(a.perPersonnelId),
				);
				const duration = a.unassignedDate
					? daysBetween(a.assignedDate, a.unassignedDate)
					: daysBetween(a.assignedDate, new Date().toISOString());
				return [
					officer?.label || "—",
					officer?.badge || officer?.badgeNum || "—",
					a.division || officer?.department || "—",
					formatDate(a.assignedDate),
					a.unassignedDate ? formatDate(a.unassignedDate) : "Current",
					`${duration}d`,
					(a.notes || "").slice(0, 50) +
						(a.notes?.length > 50 ? "…" : ""),
				];
			}),
		);

		// ─── License Expiration Formatting
		const licenseExpDisplay = vehicle.licenseExpiration
			? (() => {
					const [y, m] = vehicle.licenseExpiration.split("-");
					return `${m}/${y}`;
				})()
			: "—";

		// ─── Build HTML Document
		const html = `<!DOCTYPE html><html><head>
<meta charset="utf-8">
<title>Vehicle Report — ${vehicle.unitNumber}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #1a1a1a; background: #fff; padding: 24px; }
  h1 { font-size: 20px; font-weight: 800; letter-spacing: 0.01em; margin-bottom: 2px; }
  .subtitle { font-size: 13px; color: #555; margin-bottom: 4px; }
  .meta-row { font-size: 10px; color: #888; margin-bottom: 18px; }
  .badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: 700; border: 1px solid #ccc; background: #f4f4f4; }
  .section-head { background: #1a1a1a; color: #fff; padding: 5px 10px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; margin: 18px 0 8px; border-radius: 3px; }
  .fields-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px 16px; margin-bottom: 4px; }
  .field-row { display: flex; flex-direction: column; }
  .field-label { font-size: 9px; font-weight: 700; text-transform: uppercase; color: #888; letter-spacing: 0.06em; margin-bottom: 1px; }
  .field-value { font-size: 11px; color: #1a1a1a; }
  table { width: 100%; border-collapse: collapse; font-size: 10px; margin-bottom: 4px; }
  thead tr { background: #f0f0f0; }
  th { text-align: left; padding: 4px 6px; font-weight: 700; font-size: 9px; text-transform: uppercase; letter-spacing: 0.05em; color: #555; border-bottom: 1px solid #ddd; }
  td { padding: 4px 6px; border-bottom: 1px solid #eee; vertical-align: top; }
  tr:nth-child(even) td { background: #fafafa; }
  .stat-row { display: flex; gap: 24px; margin-bottom: 10px; }
  .stat-box { flex: 1; padding: 8px 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9; }
  .stat-num { font-size: 16px; font-weight: 800; }
  .stat-label { font-size: 9px; text-transform: uppercase; color: #888; letter-spacing: 0.05em; }
  .empty-note { color: #aaa; font-style: italic; font-size: 10px; padding: 4px 0; }
  .print-footer { position: fixed; bottom: 0; left: 0; right: 0; border-top: 1px solid #ddd; padding: 4px 12px; font-size: 9px; color: #aaa; background: #fff; display: flex; justify-content: space-between; }
  .alert-box { padding: 8px 12px; border-radius: 4px; margin-bottom: 12px; font-size: 11px; font-weight: 600; }
  .alert-error { background: #fee; border: 1px solid #fcc; color: #c00; }
  .alert-warning { background: #fff3cd; border: 1px solid #ffd966; color: #856404; }
  @media print {
    body { padding: 12px; }
    .section-head { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    thead tr { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    tr:nth-child(even) td { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .stat-box { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .alert-box { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    @page { margin: 14mm 12mm 20mm; }
  }
</style>
</head><body>

<h1>${vehicleDisplay}</h1>
<div class="subtitle">${d(vehicle.color)} ${d(vehicle.bodyStyle)} · VIN: ${d(vehicle.vin)} · <span class="badge">${vehicle.status || "—"}</span></div>
<div class="meta-row">Printed ${new Date().toLocaleString()} · Printed by ${printedBy} · Division: ${d(vehicle.division)} · Assigned to: ${empDisplay}</div>

${
	hasOverdueService
		? `<div class="alert-box alert-error">🔴 Service Overdue — Immediate attention required</div>`
		: ""
}
${
	hasDueSoonService && !hasOverdueService
		? `<div class="alert-box alert-warning">🟡 Service Due Soon</div>`
		: ""
}
${
	unresolvedInspections.length > 0
		? `<div class="alert-box alert-warning">⚠️ ${unresolvedInspections.length} Unresolved Inspection${unresolvedInspections.length > 1 ? "s" : ""}</div>`
		: ""
}

${sectionHead("Vehicle Details")}
<div class="fields-grid">
  ${fieldRow("License Plate", d(vehicle.licenseNumber))}
  ${fieldRow("License Expiration", licenseExpDisplay)}
  ${fieldRow("Color", d(vehicle.color))}
  ${fieldRow("Body Style", d(vehicle.bodyStyle))}
  ${fieldRow("Division", d(vehicle.division))}
  ${fieldRow("Odometer", fmtMi(vehicle.odometer))}
  ${fieldRow("Starting Mileage", fmtMi(vehicle.startingMileage))}
  ${fieldRow("Total Miles", fmtMi(totalMiles))}
  ${fieldRow("Original Cost", fmtMoney(vehicle.originalCost))}
  ${fieldRow("Funding Source", fundingSourceName)}
  ${fieldRow("Purchase Date", vehicle.purchaseDate ? formatDate(vehicle.purchaseDate) : "—")}
  ${fieldRow("Status", vehicle.status || "—")}
</div>
${vehicle.notes ? `<div style="margin-top:8px;padding:6px 10px;background:#f8f8f8;border-left:3px solid #ddd;font-size:11px;">${vehicle.notes}</div>` : ""}

${sectionHead("Work Order Summary")}
<div class="stat-row">
  <div class="stat-box"><div class="stat-num">${fmtMoney(totalWOCost)}</div><div class="stat-label">Total Cost</div></div>
  <div class="stat-box"><div class="stat-num">${vehWOs.length}</div><div class="stat-label">Total Orders</div></div>
  <div class="stat-box"><div class="stat-num">${openWOs}</div><div class="stat-label">Open</div></div>
  <div class="stat-box"><div class="stat-num">${closedWOs}</div><div class="stat-label">Closed</div></div>
</div>
${woTable}

${sectionHead(`Equipment (${equipment.length})`)}
${eqTable}

${sectionHead(`Inspections (${vehInsps.length}${vehInsps.length > 20 ? " — showing most recent 20" : ""})`)}
${inspTable}

${sectionHead("Service Requirements")}
${svcTable}

${sectionHead(`Assignment History (${vehAssignments.length})`)}
${assignTable}

${sectionHead("Mileage Summary")}
<div class="fields-grid">
  ${fieldRow("Starting Mileage", fmtMi(vehicle.startingMileage))}
  ${fieldRow("Current Odometer", fmtMi(vehicle.odometer))}
  ${fieldRow("Total Miles Driven", fmtMi(totalMiles))}
  ${fieldRow(
		"Average Miles/Day",
		vehicle.purchaseDate
			? (() => {
					const days = daysBetween(
						vehicle.purchaseDate,
						new Date().toISOString(),
					);
					const avgPerDay =
						days > 0 ? Math.round(totalMiles / days) : 0;
					return `${avgPerDay.toLocaleString()} mi`;
				})()
			: "—",
  )}
</div>

<div class="print-footer">
  <span>Asset Management · ${vehicleDisplay} · Printed ${new Date().toLocaleString()}</span>
  <span>Printed by ${printedBy}</span>
</div>

<script>window.onload = function() { window.print(); };</script>
</body></html>`;

		const w = window.open("", "_blank", "width=900,height=700");
		if (w) {
			w.document.write(html);
			w.document.close();
		}
	};

	if (!vehicle) return null;

	const totalMiles = (vehicle.odometer || 0) - (vehicle.startingMileage || 0);

	const tabs = [
		{ id: 0, label: "Work Orders", icon: "🔧", count: vehWOs.length },
		{ id: 1, label: "Equipment", icon: "🔫", count: equipment.length },
		{
			id: 2,
			label: "Inspections",
			icon:
				unresolvedInspections.length > 0
					? "⚠️"
					: vehInsps.length > 0
						? "✅"
						: "📋",
			count: vehInsps.length,
			badge:
				unresolvedInspections.length > 0
					? unresolvedInspections.length
					: null,
			badgeColor: "warning",
		},
		{
			id: 3,
			label: "Service Due",
			icon: hasOverdueService ? "🔴" : hasDueSoonService ? "🟡" : "⚙️",
			count:
				svcStatuses.filter((s) =>
					["Overdue", "Due Soon"].includes(s.status),
				).length || null,
		},
		{
			id: 4,
			label: "Assignment History",
			icon: "👤",
			count: vehAssignments.length,
		},
		{ id: 5, label: "Notes", icon: "📝", count: 0 },
	];

	return (
		<Box className={classes.root}>
			{/* Overdue Alert */}
			{hasOverdueService && (
				<Alert
					severity="error"
					onClick={() => setTab(3)}
					sx={{ cursor: "pointer", mb: 2 }}
				>
					🔧 Service overdue — click to view Service Due tab
				</Alert>
			)}

			{/* Unresolved Inspections Alert */}
			{unresolvedInspections.length > 0 && !hasOverdueService && (
				<Alert
					severity="warning"
					onClick={() => setTab(2)}
					sx={{ cursor: "pointer", mb: 2 }}
				>
					⚠️ {unresolvedInspections.length} unresolved inspection
					{unresolvedInspections.length > 1 ? "s" : ""} require
					attention — click to view
				</Alert>
			)}

			{/* Header */}
			<Box className={classes.header}>
				<Box>
					<Box className={classes.headerLeft}>
						<Typography className={classes.unitNumber}>
							{vehicle.unitNumber}
						</Typography>
						<Typography className={classes.vehicleTitle}>
							{vehicle.year} {vehicle.make} {vehicle.model}
						</Typography>
						<StatusBadge status={vehicle.status} />
						<Typography
							variant="caption"
							sx={{
								fontFamily: "monospace",
								color: "text.secondary",
							}}
						>
							VIN: {vehicle.vin}
						</Typography>
					</Box>
					<Box className={classes.metaStrip}>
						<span className={classes.metaPill}>
							<span className={classes.metaPillLabel}>Div</span>
							{vehicle.division || "—"}
						</span>
						<span className={classes.metaPill}>
							<span className={classes.metaPillLabel}>
								Employee
							</span>
							{getEmployeeDisplay(vehicle.perPersonnelId)}
						</span>
						<span className={classes.metaPill}>
							<span className={classes.metaPillLabel}>
								Odometer
							</span>
							<span style={{ fontFamily: "monospace" }}>
								{fmtMi(vehicle.odometer)}
							</span>
						</span>
						{totalMiles > 0 && (
							<span className={classes.metaPill}>
								<span className={classes.metaPillLabel}>
									Total Mi
								</span>
								<span
									style={{
										fontFamily: "monospace",
										color:
											totalMiles > 150000
												? theme.palette.error.main
												: "inherit",
									}}
								>
									{fmtMi(totalMiles)}
								</span>
							</span>
						)}
						{vehicle.originalCost && (
							<span className={classes.metaPill}>
								<span className={classes.metaPillLabel}>
									Cost
								</span>
								<span style={{ fontFamily: "monospace" }}>
									{fmtMoney(vehicle.originalCost)}
								</span>
							</span>
						)}
					</Box>
				</Box>

				<Box sx={{ display: "flex", gap: 1 }}>
					<Button
						variant="outlined"
						startIcon={<ArrowBack />}
						onClick={onBack}
					>
						{backLabel}
					</Button>
					<Button
						variant="outlined"
						startIcon={<Edit />}
						onClick={() => onEdit(vehicle)}
					>
						Edit
					</Button>
					<Button
						variant="outlined"
						startIcon={<Print />}
						onClick={handlePrint}
					>
						Print
					</Button>
				</Box>
			</Box>

			{/* Mileage Chart */}
			<MileageChart
				open={mileageChartOpen}
				onToggle={() => setMileageChartOpen((o) => !o)}
				chartType={mileageChartType}
				onChartTypeChange={setMileageChartType}
				dataPoints={mileageDataPoints}
				vehicle={vehicle}
			/>

			{/* Tabs */}
			<Box className={classes.tabsContainer}>
				<Tabs
					value={tab}
					onChange={(_, v) => setTab(v)}
					variant="scrollable"
					scrollButtons="auto"
				>
					{tabs.map((t) => (
						<Tab
							key={t.id}
							label={
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
									}}
								>
									{t.icon} {t.label}
									{t.badge != null && (
										<Chip
											label={t.badge}
											size="small"
											color={t.badgeColor || "default"}
											sx={{ height: 18, fontSize: 10 }}
										/>
									)}
									{t.count > 0 && !t.badge && (
										<Chip
											label={t.count}
											size="small"
											color={
												t.id === 3 && hasOverdueService
													? "error"
													: t.id === 3 &&
														  hasDueSoonService
														? "warning"
														: "default"
											}
										/>
									)}
								</Box>
							}
						/>
					))}
				</Tabs>
			</Box>

			{/* Tab Panels */}
			<Box className={classes.tabPanel}>
				<TabPanel value={tab} index={0}>
					<WorkOrdersTab
						workOrders={vehWOs}
						vehicle={vehicle}
						employees={employees}
						totalCost={totalWOCost}
						openCount={openWOs}
						closedCount={closedWOs}
						woChartOpen={woChartOpen}
						setWoChartOpen={setWoChartOpen}
						woChartType={woChartType}
						setWoChartType={setWoChartType}
						onAddWO={handleAddWO}
						onViewWO={handleViewWO}
						onEditWO={handleEditWO}
						onDeleteWO={handleDeleteWO}
						onCloseWO={handleCloseWO}
						onReopenWO={handleReopenWO}
						classes={classes}
					/>
				</TabPanel>

				{/* 
				    EQUIPMENT TAB
				     */}
				<TabPanel value={tab} index={1}>
					<EquipmentTab
						vehicle={vehicle}
						equipment={equipment}
						employees={employees}
						onViewEquipment={handleViewEquipment}
						onAssignEquipment={() => setShowAssignEquipment(true)}
						canAssign={true}
						classes={classes}
					/>
				</TabPanel>

				<TabPanel value={tab} index={2}>
					<InspectionsTab
						inspections={vehInsps}
						vehicle={vehicle}
						employees={employees}
						checklistItems={checklistItems}
						onAddInspection={handleAddInspection}
						onViewInspection={handleViewInspection}
						onEditInspection={handleEditInspection}
						onDeleteInspection={handleDeleteInspection}
						onResolveInspection={handleResolveInspection}
						onCreateWorkOrder={handleCreateWorkOrderFromInspection}
						inspChartOpen={inspChartOpen}
						setInspChartOpen={setInspChartOpen}
						inspChartType={inspChartType}
						setInspChartType={setInspChartType}
						classes={classes}
					/>
				</TabPanel>

				<TabPanel value={tab} index={3}>
					<ServiceDueTab
						vehicle={vehicle}
						svcStatuses={svcStatuses}
						onCreateWO={handleCreateWOFromService}
						classes={classes}
					/>
				</TabPanel>

				<TabPanel value={tab} index={4}>
					<AssignmentHistoryTab
						vehicle={vehicle}
						assignmentHistory={assignmentHistory}
						classes={classes}
						onAssignmentChange={() => {
							if (refreshList) refreshList();
							refreshAssignmentHistory();
						}}
					/>
				</TabPanel>

				<TabPanel value={tab} index={5}>
					<NotesTab vehicle={vehicle} classes={classes} />
				</TabPanel>
			</Box>

			{/* 
			    WORK ORDER DIALOGS
			     */}

			<AddEditWorkOrderDialog
				open={showWODialog}
				onClose={() => {
					setShowWODialog(false);
					setSelectedWO(null);
				}}
				onSave={handleSaveWO}
				initialData={selectedWO}
				vehicleOptions={[vehicleOption]}
				workOrderTypes={workOrderTypes}
				statusOptions={statusOptions}
				employeeOptions={employees}
				fundingSourceOptions={fundingSources}
				vehicleStatusOptions={vehicleStatusOptions}
				serviceRequirements={serviceRequirements || []}
			/>

			<AddEditWorkOrderDialog
				open={showViewWODialog}
				onClose={() => {
					setShowViewWODialog(false);
					setSelectedWO(null);
				}}
				initialData={selectedWO}
				viewOnly={true}
				vehicleOptions={[vehicleOption]}
				workOrderTypes={workOrderTypes}
				statusOptions={statusOptions}
				employeeOptions={employees}
				fundingSourceOptions={fundingSources}
				vehicleStatusOptions={vehicleStatusOptions}
				serviceRequirements={serviceRequirements || []}
			/>

			<DeleteConfirmationDialog
				open={showDeleteWODialog}
				onClose={() => {
					setShowDeleteWODialog(false);
					setSelectedWO(null);
				}}
				onConfirm={handleConfirmDeleteWO}
				title="Delete Work Order"
				message={`Are you sure you want to delete work order WO-${String(
					selectedWO?.id || "",
				)
					.slice(-4)
					.padStart(4, "0")}?`}
			/>

			<CloseWorkOrderDialog
				open={showCloseWODialog}
				onClose={() => {
					setShowCloseWODialog(false);
					setSelectedWO(null);
				}}
				onConfirm={handleConfirmCloseWO}
				workOrder={selectedWO}
				vehicleOptions={[vehicleOption]}
				vehicleStatusOptions={vehicleStatusOptions}
			/>

			{/* 
			    INSPECTION DIALOGS
			     */}

			<AddEditInspectionDialog
				open={showInspectionDialog}
				onClose={() => {
					setShowInspectionDialog(false);
					setSelectedInspection(null);
					setInspectionMode("create");
				}}
				onSave={handleSaveInspection}
				initialData={
					inspectionMode === "edit"
						? selectedInspection
						: {
								vehicleId: vehicle.id,
								odometer: vehicle.odometer,
							}
				}
				vehicles={[vehicleOption]}
				employees={employees}
				checklistItems={checklistItems}
				inspectionTypeOptions={inspectionTypeOptions}
			/>

			<InspectionDetailDialog
				open={showInspectionDetail}
				onClose={() => {
					setShowInspectionDetail(false);
					setSelectedInspection(null);
				}}
				inspection={selectedInspection}
				vehicles={vehicles.length > 0 ? vehicles : [vehicleOption]}
				employees={employees}
				checklistItems={checklistItems}
				fundingSources={fundingSources}
				onResolveInspection={handleResolveInspectionFromDetail}
				onCreateWorkOrder={(woData) => {
					setShowInspectionDetail(false);
					const notes = woData.notes
						? woData.notes
						: `Follow-up from inspection INS-${String(
								selectedInspection?.id || "",
							)
								.slice(-4)
								.padStart(4, "0")}`;

					setSelectedWO({
						vehicleId: vehicle.id,
						type: woData.type || "Repair",
						status: "Open",
						vendor: woData.vendor,
						cost: woData.cost,
						notes: notes,
						fundingSourceId: woData.fundingSourceID,
						inspectionId: selectedInspection?.id,
					});
					setShowWODialog(true);
				}}
				workOrderTypeOptions={workOrderTypes}
				fundingSourceOptions={fundingSources}
			/>

			<DeleteConfirmationDialog
				open={showDeleteInspectionDialog}
				onClose={() => {
					setShowDeleteInspectionDialog(false);
					setInspectionToDelete(null);
				}}
				onConfirm={handleConfirmDeleteInspection}
				title="Delete Inspection"
				message={`Are you sure you want to delete inspection INS-${String(
					inspectionToDelete?.id || "",
				)
					.slice(-4)
					.padStart(4, "0")}?`}
			/>

			{/* 
			    EQUIPMENT DIALOGS
			     */}

			{/* Assign Equipment Dialog */}
			<AssignEquipmentDialog
				open={showAssignEquipment}
				onClose={() => setShowAssignEquipment(false)}
				onSave={handleAssignEquipment}
				vehicle={vehicle}
				equipment={allEquipment}
				employees={employees}
			/>

			{/* View Equipment Dialog - Using AddEditEquipmentDialog with viewOnly=true */}
			<AddEditEquipmentDialog
				open={!!viewEquipment}
				onClose={() => setViewEquipment(null)}
				onSave={() => {}} // No-op since viewOnly
				initialData={viewEquipment}
				viewOnly={true}
				statusOptions={equipmentStatusOptions}
				typeOptions={equipmentTypeOptions}
				categoryCodeOptions={equipmentCategoryOptions}
				conditionOptions={equipmentConditionOptions}
				divisionOptions={divisionOptions}
				employeeOptions={employees}
				vehicleOptions={vehicleOptions}
				fundingSourceOptions={fundingSources}
				serviceRequirements={equipmentServiceRequirements}
				assignmentHistory={viewEquipment?.assignmentHistory || []}
				maintenanceRecords={viewEquipment?.maintenanceRecords || []}
			/>
		</Box>
	);
};

export default VehicleDetail;
